import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  RootContainer,
  DropZone,
  Gallery,
  AddMoreButton,
  FilePreviewGrid, FileActionsOverlay,
} from '@securspace/securspace-ui-kit';
import type { StagedFile, ValidationError } from '@securspace/securspace-ui-kit';
import { CircularProgress, IconButton, styled } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CropIcon from '@mui/icons-material/Crop';
import Delete from '@mui/icons-material/Delete';
import ImagePreviewModal, {type ImagePreviewAction} from './ImagePreviewModal';
import CropPanel from './CropPanel';
import { centerCropImageFile, toCroppedFile } from './cropUtils';
import { useSnackbar } from '../../context/snackbar-context';

/** Accepted upload MIME types — mirrors the legacy uploader (PNG, JPG, GIF). */
const ACCEPTED_FILE_TYPES = ['image/png', 'image/jpeg', 'image/gif'];

/** 20 MB — matches the legacy uploader's limit (UIkit does not enforce size). */
const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;

// Short enough for the small "+" tile; the dashed drop zone itself signals
// that photos can be dragged in, and clicking it opens the file picker.
const DROP_PROMPT_SINGLE = 'Add photo';
const DROP_PROMPT_MULTIPLE = 'Add photos';

const CROP_APPLY_FAILED_MESSAGE = 'Crop failed. Please try again.';

const NO_ACTIONS: ImagePreviewAction[] = [];
const NO_EXISTING_IMAGES: ExistingImage[] = [];

/**
 * Reusable styled icon button for image actions (edit/remove) with white border and circular shape.
 */
const ActionIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.common.white,
  border: '2px solid #fff',
  borderRadius: '50%',
}));

export interface ExistingImage {
  fileName: string;
  url: string;
  /** A re-crop of this image, shown in its slot until the caller uploads it in its place. */
  replacement?: File;
}

interface AspectRatioImageUploadProps {
  /** Allow selecting multiple files (gallery) vs a single file (listing image). */
  multiple: boolean;
  /** Shape (width / height) every added photo is center-cropped to, and the
   *  Crop panel is locked to — e.g. `4 / 3`. */
  aspectRatio: number;
  /** Currently selected (not-yet-uploaded) files. */
  files: File[];
  onFilesChange: (files: File[]) => void;
  /** Maximum number of images total (existing + newly-selected). */
  maxFiles?: number;
  /** Already-persisted images to show as thumbnails (edit mode). */
  existingImages?: ExistingImage[];
  /** Called when the user removes an already-persisted image. Omit to keep
   *  existing images read-only (no trash icon shown for them). */
  onRemoveExisting?: (image: ExistingImage) => void;
  /** Called when the user re-crops an already-persisted gallery image: the
   *  caller stages `file` to replace it in place. Omit to keep gallery images
   *  uncroppable. (The single listing slot replaces via `onFilesChange`.) */
  onReplaceExisting?: (image: ExistingImage, file: File) => void;
  /** Called with `true` while added photos are still being center-cropped and
   *  `false` once they're all ready. Until then the caller holds the uncropped
   *  photo, so a caller that must only save cropped bytes should wait. */
  onPreparingChange?: (preparing: boolean) => void;
}

/**
 * Best-effort MIME type from a stored file name, used only to build the
 * placeholder `File` for an already-persisted image so the UIkit grid can
 * render its thumbnail. The bytes are never read or uploaded.
 */
const inferTypeFromFileName = (fileName: string): string => {
  const ext = fileName.split('.').pop()?.toLowerCase();
  if (ext === 'png') return 'image/png';
  if (ext === 'gif') return 'image/gif';
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
  return '';
};

/**
 * Drag-and-drop image upload zone backed by the securspace-ui-kit `ImageUpload`
 * components that keeps every photo at a fixed `aspectRatio`. Used for the
 * Listing Image (single) and Image Gallery (multiple) sections of the
 * Create/Edit Location wizard, and the Company Logo on the company profile.
 *
 * The caller owns the not-yet-uploaded files as a plain `File[]`; this wrapper
 * bridges that to the UIkit's caller-owned `StagedFile[]` model (owning each
 * `previewUrl` and its object-URL lifecycle) and seeds any already-persisted
 * images as read-only thumbnails.
 *
 * Each added photo is center-cropped to `aspectRatio` before it reaches the
 * caller, and the preview's Crop action lets the user move a box of that
 * shape over the original photo. Cropping an already-persisted image stages
 * the crop in that image's place.
 *
 * Rejects files over 20 MB or of an unsupported type with a toast, and caps the
 * number of selected files when `maxFiles` is provided.
 */
const AspectRatioImageUpload = (props: AspectRatioImageUploadProps) => {
  const { multiple, aspectRatio, files, onFilesChange, maxFiles, existingImages = NO_EXISTING_IMAGES, onRemoveExisting, onReplaceExisting, onPreparingChange } = props;
  const { snackbarShowMessage } = useSnackbar();

  // Caller owns the preview modal's open/close state (per the UIKit contract):
  // the pencil action stages a file here; the modal renders while it is set.
  const [previewingFile, setPreviewingFile] = useState<StagedFile | null>(null);
  const previewingFileRef = useRef<StagedFile | null>(null);
  const ownedPreviewUrlsRef = useRef<Set<string>>(new Set());

  // Cropping is async, so handlers read the latest `files` rather than the
  // render-time value (the caller may have changed it in the meantime).
  const filesRef = useRef(files);
  filesRef.current = files;

  // Each cropped file → the photo as the supplier picked it, plus an object URL
  // for each original (created when the Crop panel first opens) so a re-crop
  // starts from the full photo rather than the already-trimmed one.
  const originalByFileRef = useRef<Map<File, File>>(new Map());
  const cropSourceUrlsRef = useRef<Map<File, string>>(new Map());
  // A file cropped from an already-persisted image → that image's hosted URL,
  // so re-cropping it also starts from the stored photo.
  const hostedSourceByFileRef = useRef<Map<File, string>>(new Map());
  // A staged re-crop of a persisted gallery image → its preview URL.
  const replacementUrlsRef = useRef<Map<File, string>>(new Map());

  useEffect(() => {
    previewingFileRef.current = previewingFile;
  }, [previewingFile]);

  // Forgets a file's original, revoking the original's crop-source URL once no
  // remaining file was cropped from it.
  const releaseOriginal = (file: File) => {
    const originals = originalByFileRef.current;
    const original = originals.get(file);
    originals.delete(file);
    if (!original || [...originals.values()].includes(original)) return;
    const sourceUrl = cropSourceUrlsRef.current.get(original);
    if (sourceUrl) {
      URL.revokeObjectURL(sourceUrl);
      cropSourceUrlsRef.current.delete(original);
    }
  };

  // Build object-URL previews for newly-added files, keyed by File identity so a
  // URL persists across renders unless its File actually changes. This prevents
  // revoking a blob URL that a thumbnail — or an open preview modal — is still
  // displaying (the `files` prop array identity may change on every parent render).
  const previewUrlsRef = useRef<Map<File, string>>(new Map());
  const [newPreviews, setNewPreviews] = useState<StagedFile[]>([]);

  // A single newly-added photo opens straight in the cropper once its
  // preview exists, so the supplier frames it rather than accepting the
  // center crop blind. Several at once keep the center crop (adjust via the pencil).
  const [fileToCropOnAdd, setFileToCropOnAdd] = useState<File | null>(null);
  // Photos still being center-cropped. Their pencil stays disabled until the
  // crop is swapped in, so an open preview never points at a replaced file.
  const [preparingFiles, setPreparingFiles] = useState<Set<File>>(() => new Set());
  const [openInCrop, setOpenInCrop] = useState(false);

  const isPreparing = preparingFiles.size > 0;
  const onPreparingChangeRef = useRef(onPreparingChange);
  onPreparingChangeRef.current = onPreparingChange;
  useEffect(() => {
    onPreparingChangeRef.current?.(isPreparing);
  }, [isPreparing]);

  useEffect(() => {
    const cache = previewUrlsRef.current;
    const next: StagedFile[] = files.map((file) => {
      let previewUrl = cache.get(file);
      if (!previewUrl) {
        previewUrl = URL.createObjectURL(file);
        cache.set(file, previewUrl);
        ownedPreviewUrlsRef.current.add(previewUrl);
      }
      return { file, previewUrl };
    });
    // Revoke and forget URLs for files that are no longer present.
    cache.forEach((previewUrl, file) => {
      if (!files.includes(file)) {
        if (ownedPreviewUrlsRef.current.delete(previewUrl)) {
          URL.revokeObjectURL(previewUrl);
        }
        cache.delete(file);
        releaseOriginal(file);
        hostedSourceByFileRef.current.delete(file);
      }
    });
    setNewPreviews(next);
  }, [files]);

  // Revoke every remaining object URL on unmount.
  useEffect(
    () => () => {
      ownedPreviewUrlsRef.current.forEach((previewUrl) => URL.revokeObjectURL(previewUrl));
      ownedPreviewUrlsRef.current.clear();
      previewUrlsRef.current.clear();
      cropSourceUrlsRef.current.forEach((sourceUrl) => URL.revokeObjectURL(sourceUrl));
      cropSourceUrlsRef.current.clear();
      originalByFileRef.current.clear();
      hostedSourceByFileRef.current.clear();
      replacementUrlsRef.current.forEach((previewUrl) => URL.revokeObjectURL(previewUrl));
      replacementUrlsRef.current.clear();
    },
    []
  );

  // An already-persisted image only has a file name + hosted URL, but the UIKit's
  // `StagedFile` needs a `File` and a `previewUrl`. So for each existing image we make a
  // placeholder `File` (used just as a stand-in) and use its hosted URL as the `previewUrl`.
  //
  // `byFile` maps each placeholder back to its `ExistingImage` so that removing a thumbnail
  // can look up which persisted image to delete. Placeholders are recreated whenever
  // `existingImages` changes, but this memo keeps them stable for the current render.
  // A persisted image with a staged re-crop previews the crop in its own slot.
  const { existingStaged, existingImageByFile } = useMemo(() => {
    const byFile = new Map<File, ExistingImage>();
    const staged: StagedFile[] = existingImages.map((image) => {
      const file = new File([], image.fileName, { type: inferTypeFromFileName(image.fileName) });
      byFile.set(file, image);
      let previewUrl = image.url;
      if (image.replacement) {
        previewUrl = replacementUrlsRef.current.get(image.replacement) ?? URL.createObjectURL(image.replacement);
        replacementUrlsRef.current.set(image.replacement, previewUrl);
      }
      return { file, previewUrl };
    });
    return { existingStaged: staged, existingImageByFile: byFile };
  }, [existingImages]);

  // Revoke a staged re-crop's preview once the caller drops it (saved or removed).
  useEffect(() => {
    const staged = new Set(existingImages.map((image) => image.replacement));
    replacementUrlsRef.current.forEach((previewUrl, file) => {
      if (!staged.has(file)) {
        URL.revokeObjectURL(previewUrl);
        replacementUrlsRef.current.delete(file);
      }
    });
  }, [existingImages]);
  const existingImageByFileRef = useRef(existingImageByFile);
  existingImageByFileRef.current = existingImageByFile;

  // In single (Listing Image) mode a newly-added file supersedes any existing
  // saved image, so the one slot never shows two thumbnails in edit mode.
  const stagedFiles: StagedFile[] = useMemo(
    () =>
      !multiple && newPreviews.length > 0
        ? newPreviews
        : [...existingStaged, ...newPreviews],
    [multiple, existingStaged, newPreviews]
  );

  // The original photo to crop from: the stored photo for a persisted image (or
  // a crop of one), the picked photo for a new upload, or undefined when the
  // file wasn't trimmed on upload (its preview already shows the whole photo).
  const getCropSource = (file: File): string | undefined => {
    const hostedUrl = existingImageByFile.get(file)?.url ?? hostedSourceByFileRef.current.get(file);
    if (hostedUrl) return hostedUrl;
    const original = originalByFileRef.current.get(file);
    if (!original || original === file) return undefined;
    let sourceUrl = cropSourceUrlsRef.current.get(original);
    if (!sourceUrl) {
      sourceUrl = URL.createObjectURL(original);
      cropSourceUrlsRef.current.set(original, sourceUrl);
    }
    return sourceUrl;
  };
  const getCropSourceRef = useRef(getCropSource);
  getCropSourceRef.current = getCropSource;

  // Stable identity (the modal warns otherwise); reads the crop source via a ref.
  const previewActions = useMemo<ImagePreviewAction[]>(() => [
    {
      id: 'crop',
      label: 'Crop',
      icon: <CropIcon/>,
      panel: (file, onComplete, onCancel, onError, fullScreen) => (
        <CropPanel
          file={file}
          src={getCropSourceRef.current(file.file)}
          aspectRatio={aspectRatio}
          onComplete={onComplete}
          onCancel={onCancel}
          onError={onError}
          fullScreen={fullScreen}
        />
      ),
    },
  ], [aspectRatio]);

  const handleFilesAdded = async (added: File[]) => {
    if (!added || added.length === 0) return;

    const withinSizeLimit = added.filter((file) => file.size <= MAX_FILE_SIZE_BYTES);
    const oversizedCount = added.length - withinSizeLimit.length;
    if (oversizedCount > 0) {
      snackbarShowMessage(
        oversizedCount === 1
          ? 'That image is too large. Please choose a file under 20 MB.'
          : `${oversizedCount} images are too large. Please choose files under 20 MB.`,
        'warning'
      );
    }
    if (withinSizeLimit.length === 0) return;

    const addedFiles = multiple ? withinSizeLimit : withinSizeLimit.slice(0, 1);
    // Hand the photos to the caller right away so a save or step change during
    // cropping can't miss them; each is swapped for its center crop once that's
    // ready. A save in between uploads the uncropped photo rather than none,
    // unless the caller waits for `onPreparingChange(false)`.
    onFilesChange(multiple ? [...filesRef.current, ...addedFiles] : addedFiles);
    setPreparingFiles((current) => new Set([...current, ...addedFiles]));

    // One photo at a time: each crop decodes the full-resolution image, so
    // cropping a batch of up to 10 at once could hold several hundred MB and
    // crash a phone tab. Each crop is swapped in as soon as it's ready.
    let lastFile: File | null = null;
    for (const original of addedFiles) {
      let cropped = original;
      try {
        // eslint-disable-next-line no-await-in-loop
        cropped = await centerCropImageFile(original, aspectRatio);
      } catch {
        // Keep the uncropped photo (still adjustable via the pencil) so the
        // photo never stays stuck in "preparing".
      }
      // Only swap photos the caller still has (not saved or removed meanwhile).
      const current = filesRef.current;
      lastFile = current.includes(original) ? original : null;
      if (lastFile && cropped !== original) {
        originalByFileRef.current.set(cropped, original);
        const next = current.map((file) => (file === original ? cropped : file));
        // Keep the next iteration's view current even before the caller re-renders.
        filesRef.current = next;
        onFilesChange(next);
        lastFile = cropped;
      }
      setPreparingFiles((preparing) => {
        const next = new Set(preparing);
        next.delete(original);
        return next;
      });
    }
    if (addedFiles.length === 1 && lastFile) {
      setFileToCropOnAdd(lastFile);
    }
  };

  const handleValidationFailed = (error: ValidationError) => {
    if (error.code === 'INVALID_FILE_TYPE') {
      snackbarShowMessage('Unsupported file type. Please upload a PNG, JPG or GIF image.', 'warning');
    } else if (error.code === 'MAX_FILES_EXCEEDED' && maxFiles) {
      snackbarShowMessage(`You can add up to ${maxFiles} images total.`, 'warning');
    } else {
      snackbarShowMessage(error.message, 'warning');
    }
  };

  const handlePreview = (file: StagedFile) => {
    if (preparingFiles.has(file.file)) return;
    previewingFileRef.current = file;
    setOpenInCrop(false);
    setPreviewingFile(file);
  };

  const closePreview = () => {
    previewingFileRef.current = null;
    setOpenInCrop(false);
    setPreviewingFile(null);
  };

  useEffect(() => {
    if (!fileToCropOnAdd) return;
    // Saved or removed before its preview existed: don't open anything.
    if (!filesRef.current.includes(fileToCropOnAdd)) {
      setFileToCropOnAdd(null);
      return;
    }
    const staged = newPreviews.find((file) => file.file === fileToCropOnAdd);
    if (!staged) return;
    setFileToCropOnAdd(null);
    previewingFileRef.current = staged;
    setOpenInCrop(true);
    setPreviewingFile(staged);
  }, [fileToCropOnAdd, newPreviews]);

  // Close the preview once its file is no longer staged here (saved, removed or
  // replaced), so it never lingers over a later step without a usable Crop.
  useEffect(() => {
    const previewed = previewingFileRef.current?.file;
    if (previewed && !files.includes(previewed) && !existingImageByFile.has(previewed)) {
      previewingFileRef.current = null;
      setOpenInCrop(false);
      setPreviewingFile(null);
    }
  }, [files, existingImageByFile]);

  // A new upload can always be re-cropped. A persisted image can be when its
  // crop can take its place: the single slot (a new file supersedes it), or a
  // gallery whose caller stages replacements.
  const canCrop = (file: File) =>
    files.includes(file) || (existingImageByFile.has(file) && (!multiple || Boolean(onReplaceExisting)));

  // Swaps the cropped bytes in as a new File, so the crop is what uploads (not
  // just what previews). Its blob URL is adopted as the new file's preview, and
  // the superseded file's URL is revoked by the `files` effect after the
  // replacement has rendered. A crop of a persisted gallery image is staged in
  // that image's slot instead, so the gallery keeps its order.
  const handleCropComplete = async (updated: StagedFile) => {
    const isStillPreviewed = () =>
      previewingFileRef.current?.file === updated.file &&
      (filesRef.current.includes(updated.file) || existingImageByFileRef.current.has(updated.file));

    let next: File | null = null;
    if (isStillPreviewed()) {
      try {
        const blob = await fetch(updated.previewUrl).then((response) => response.blob());
        const existingImage = existingImageByFileRef.current.get(updated.file);
        const hostedUrl = existingImage?.url ?? hostedSourceByFileRef.current.get(updated.file);
        const original = originalByFileRef.current.get(updated.file) ?? updated.file;
        next = toCroppedFile(blob, existingImage?.fileName ?? original.name);
        if (hostedUrl) {
          hostedSourceByFileRef.current.set(next, hostedUrl);
        } else {
          originalByFileRef.current.set(next, original);
        }
      } catch {
        snackbarShowMessage(CROP_APPLY_FAILED_MESSAGE, 'error');
      }
    }
    if (!next || !isStillPreviewed()) {
      if (next) {
        releaseOriginal(next);
        hostedSourceByFileRef.current.delete(next);
      }
      URL.revokeObjectURL(updated.previewUrl);
      return;
    }

    const replacedFile = next;
    const existingImage = existingImageByFileRef.current.get(updated.file);
    // Confirming a crop finishes the edit; the grid thumbnail shows the result.
    closePreview();

    if (existingImage && multiple) {
      // Re-cropping reads the stored photo via the image's own URL.
      hostedSourceByFileRef.current.delete(replacedFile);
      replacementUrlsRef.current.set(replacedFile, updated.previewUrl);
      onReplaceExisting?.(existingImage, replacedFile);
      return;
    }

    previewUrlsRef.current.set(replacedFile, updated.previewUrl);
    ownedPreviewUrlsRef.current.add(updated.previewUrl);
    if (!existingImage) {
      onFilesChange(filesRef.current.map((file) => (file === updated.file ? replacedFile : file)));
    } else {
      // The new listing file supersedes the stored one; the save deletes it.
      onFilesChange([replacedFile]);
    }
  };

  // A staged file is removable if it's a newly-added file (present in the
  // caller-owned `files` prop) or an already-persisted image whose removal
  // the caller has opted into via `onRemoveExisting`.
  const isRemovable = (file: StagedFile) =>
    files.includes(file.file) || (Boolean(onRemoveExisting) && existingImageByFile.has(file.file));

  const handleRemove = (file: StagedFile) => {
    if (files.includes(file.file)) {
      onFilesChange(files.filter((f) => f !== file.file));
    } else {
      const existingImage = existingImageByFile.get(file.file);
      if (existingImage && onRemoveExisting) {
        onRemoveExisting(existingImage);
      } else {
        return;
      }
    }
    // Compare by the underlying File reference — `previewingFile` wraps a
    // freshly-built StagedFile each render, so wrapper identity never matches.
    if (previewingFile?.file === file.file) {
      closePreview();
    }
  };

  const renderActions = (file: StagedFile) => {
    return <FileActionsOverlay>
      {preparingFiles.has(file.file) ? (
        <ActionIconButton
          size="large"
          disabled
          aria-label="Preparing image"
          sx={{ '&.Mui-disabled': { color: 'common.white', borderColor: 'common.white' } }}
        >
          <CircularProgress size={24} color="inherit" />
        </ActionIconButton>
      ) : (
        <ActionIconButton
          size="large"
          onClick={() => handlePreview(file)}
          aria-label="Preview image"
        >
          <EditIcon />
        </ActionIconButton>
      )}
      {isRemovable(file) && (
        <ActionIconButton
          size="large"
          onClick={() => handleRemove(file)}
          aria-label="Remove image"
        >
          <Delete />
        </ActionIconButton>
      )}
    </FileActionsOverlay>
  };

  return (
    <RootContainer
      files={stagedFiles}
      onFilesAdded={handleFilesAdded}
      onValidationFailed={handleValidationFailed}
      acceptedFileTypes={ACCEPTED_FILE_TYPES}
      maxFiles={maxFiles}
      multiple={multiple}
    >
      <DropZone>
        <Gallery>
          <AddMoreButton label={multiple ? DROP_PROMPT_MULTIPLE : DROP_PROMPT_SINGLE} />
          <FilePreviewGrid renderActions={renderActions} />
        </Gallery>
      </DropZone>
      {previewingFile && (
        <ImagePreviewModal
          // Remount when switching between crop-first and preview-first.
          key={openInCrop ? 'crop' : 'preview'}
          file={previewingFile}
          actions={canCrop(previewingFile.file) ? previewActions : NO_ACTIONS}
          initialActionId={openInCrop ? 'crop' : undefined}
          onActionComplete={handleCropComplete}
          onClose={closePreview}
        />
      )}
    </RootContainer>
  );
};

export default AspectRatioImageUpload;
